import { gql, useQuery } from '@apollo/client';
import { RemoteData } from '@devexperts/remote-data-ts';
import { Empty, Input, TablePaginationConfig, TableProps } from 'antd';
import { pipe } from 'fp-ts/lib/pipeable';
import { ChangeEvent, Fragment, memo, useCallback, useContext, useMemo, useState } from 'react';
import { GetProductPositions, GetProductPositionsVariables } from '../../../../generated-types/GetProductPositions';
import { ProductContext, useIsMobile } from '../../../utils/context.utils';
import { colorRed, colorGreen } from '../../../utils/color.utils';
import { remoteData, useRemoteData } from '../../../utils/remote-data.utils';
import { nullable, Nullable, optional } from '../../../../../common/utils/nullable.utils';
import { TableWidget, SortOrder } from '../../layout/TableWidget';
import { PositionCard } from '../../ui-kit/PositionCard';
import { Ord, ordNumber, ordString } from 'fp-ts/lib/Ord';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import { array, ord } from 'fp-ts';
import css from './PositionsWidget.module.less';
import {
	ordStringCaseInsensitive,
	renderNumGrouped,
	ordStringLength,
	renderNumFixed2,
	renderMoney,
	renderOptional,
	renderOptionalPercent,
} from '../../../utils/string.utils';
import { toPercent } from '../../../utils/math.utils';
import { renderRemoteData } from '../../ui-kit/Loading';

export interface Position {
	instrument: Nullable<string>;
	productName: Nullable<string>;
	ticker: Nullable<string>;
	quantity: Nullable<number>;
	side: Nullable<string>;
	currency: Nullable<string>;
	openPrice: Nullable<number>;
	costWavg: Nullable<number>;
	costFifo: Nullable<number>;
	lastPrice: Nullable<number>;
	mtmBase: Nullable<number>;
	mtmBasePctOfNav: Nullable<number>;
	exposureBase: Nullable<number>;
	exposurePctOfNav: Nullable<number>;
	profitLossBase: Nullable<number>;
	alert: Nullable<string>;
	country: Nullable<string>;
	custodian: Nullable<string>;
}

interface PositionsWidgetProps {
	positions: RemoteData<Error, Position[]>;
	search: string;
	onChangeSearch: (val: string) => void;
	/**
	 * The fund's base currency, which is what the `…Base` amounts (MTM, Exposure,
	 * P&L) are denominated in — never the row's own `currency`. Undefined when the
	 * product carries no currency; the amounts then render unlabelled.
	 */
	fundCurrency?: string;
}

const ordOptionalString = optional.getOrd(ordStringCaseInsensitive);
const ordOptionalNumber = optional.getOrd(ordNumber);

const renderBaseMoney = (fundCurrency?: string) => (val: Nullable<number>) => renderMoney(val, fundCurrency);

const renderBaseMoneyColoured = (fundCurrency?: string) => (val: Nullable<number>) => {
	const color = val ? (val > 0 ? colorGreen : colorRed) : undefined;
	return <span style={{ color }}>{renderMoney(val, fundCurrency)}</span>;
};

const makeColumnConfigs = (fundCurrency?: string): NonNullable<TableProps<Position>['columns']> => [
	{
		dataIndex: 'productName',
		key: 'productName',
		sorter: true, // ord.contramap((p: Position) => p.productName)(ordOptionalString).compare,
		title: 'Position',
		fixed: 'left',
		className: css.productCol,
		defaultSortOrder: 'ascend',
		// width: adjusted dynamically based on the values length, see below
	},
	{
		dataIndex: 'ticker',
		key: 'ticker',
		sorter: true, // ord.contramap((p: Position) => p.ticker)(ordOptionalString).compare,
		title: 'Ticker',
		width: 160,
	},
	{
		dataIndex: 'instrument',
		key: 'instrument',
		sorter: true, // ord.contramap((p: Position) => p.instrument)(ordOptionalString).compare,
		title: 'Instrument',
		width: 130,
	},
	{
		dataIndex: 'side',
		key: 'side',
		sorter: true, // ord.contramap((p: Position) => p.side)(ordOptionalString).compare,
		title: 'Side',
		width: 80,
		render: (val: Nullable<string>) => {
			const s = val?.toLowerCase();
			const color = s === 'short' ? colorRed : s === 'long' ? colorGreen : undefined;
			return <span style={{ color }}>{val}</span>;
		},
	},
	{
		dataIndex: 'quantity',
		key: 'quantity',
		sorter: true, // ord.contramap((p: Position) => p.quantity)(ordOptionalNumber).compare,
		title: 'Quantity',
		align: 'right',
		width: 75,
		render: (val: Nullable<number>) => {
			const color = val ? (val > 0 ? colorGreen : colorRed) : undefined;
			return <span style={{ color }}>{renderNumGrouped(val)}</span>;
		},
	},
	{
		dataIndex: 'currency',
		key: 'currency',
		sorter: true, //  ord.contramap((p: Position) => p.currency)(ordOptionalString).compare,
		title: 'Ccy',
		width: 80,
	},
	{
		dataIndex: 'openPrice',
		key: 'openPrice',
		sorter: true, // ord.contramap((p: Position) => p.openPrice)(ordOptionalNumber).compare,
		title: 'Open Price',
		align: 'right',
		width: 140,
		render: renderNumFixed2,
	},
	{
		dataIndex: 'lastPrice',
		key: 'lastPrice',
		sorter: true,
		title: 'Last Price',
		align: 'right',
		width: 130,
		render: renderNumGrouped,
	},
	{
		// A per-unit price in the row's own currency, like Open Price — not a
		// base-currency amount. Absent from 5 of the 17 fund exports, so it renders
		// as an em dash for those funds.
		dataIndex: 'costWavg',
		key: 'costWavg',
		sorter: true,
		title: 'Cost WAVG',
		align: 'right',
		width: 140,
		render: renderNumFixed2,
	},
	{
		dataIndex: 'costFifo',
		key: 'costFifo',
		sorter: true,
		title: 'Cost FIFO',
		align: 'right',
		width: 130,
		render: renderNumFixed2,
	},
	{
		dataIndex: 'mtmBase',
		key: 'mtmBase',
		sorter: true,
		title: 'MTM',
		align: 'right',
		width: 130,
		render: renderBaseMoney(fundCurrency),
	},
	{
		dataIndex: 'mtmBasePctOfNav',
		key: 'mtmBasePctOfNav',
		sorter: true,
		title: 'MTM % of NAV',
		align: 'right',
		width: 170,
		render: renderOptionalPercent,
	},
	{
		// Signed: short positions carry a negative exposure.
		dataIndex: 'exposureBase',
		key: 'exposureBase',
		sorter: true,
		title: 'Exposure',
		align: 'right',
		width: 140,
		render: renderBaseMoney(fundCurrency),
	},
	{
		dataIndex: 'exposurePctOfNav',
		key: 'exposurePctOfNav',
		sorter: true,
		title: 'Exposure %',
		align: 'right',
		render: renderOptionalPercent,
		width: 140,
	},
	{
		dataIndex: 'profitLossBase',
		key: 'profitLossBase',
		sorter: true,
		title: 'P&L',
		align: 'right',
		width: 130,
		render: renderBaseMoneyColoured(fundCurrency),
	},
	{
		dataIndex: 'alert',
		key: 'alert',
		sorter: true, // ord.contramap((p: Position) => p.alert)(ordOptionalString).compare,
		title: 'Alert',
		width: 130,
		// The resolver maps the export's empty string to null; this turns it into
		// the em dash the rest of the table uses for missing data.
		render: renderOptional,
	},
	{
		dataIndex: 'country',
		key: 'country',
		sorter: true, // ord.contramap((p: Position) => p.country)(ordOptionalString).compare,
		title: 'Country',
		width: 115,
	},
	{
		dataIndex: 'custodian',
		key: 'custodian',
		sorter: true, // ord.contramap((p: Position) => p.custodian)(ordOptionalString).compare,
		title: 'Custodian',
		width: 130,
	},
];

const ords: Partial<Record<keyof Position, Ord<Position>>> = {
	instrument: ord.contramap((p: Position) => p.instrument)(nullable.getOrd(ordString)),
	productName: ord.contramap((p: Position) => p.productName)(nullable.getOrd(ordString)),
	ticker: ord.contramap((p: Position) => p.ticker)(nullable.getOrd(ordString)),
	quantity: ord.contramap((p: Position) => p.quantity)(nullable.getOrd(ordNumber)),
	side: ord.contramap((p: Position) => p.side)(nullable.getOrd(ordString)),
	currency: ord.contramap((p: Position) => p.currency)(nullable.getOrd(ordString)),
	openPrice: ord.contramap((p: Position) => p.openPrice)(nullable.getOrd(ordNumber)),
	costWavg: ord.contramap((p: Position) => p.costWavg)(nullable.getOrd(ordNumber)),
	costFifo: ord.contramap((p: Position) => p.costFifo)(nullable.getOrd(ordNumber)),
	lastPrice: ord.contramap((p: Position) => p.lastPrice)(nullable.getOrd(ordNumber)),
	mtmBase: ord.contramap((p: Position) => p.mtmBase)(nullable.getOrd(ordNumber)),
	mtmBasePctOfNav: ord.contramap((p: Position) => p.mtmBasePctOfNav)(nullable.getOrd(ordNumber)),
	exposureBase: ord.contramap((p: Position) => p.exposureBase)(nullable.getOrd(ordNumber)),
	exposurePctOfNav: ord.contramap((p: Position) => p.exposurePctOfNav)(nullable.getOrd(ordNumber)),
	profitLossBase: ord.contramap((p: Position) => p.profitLossBase)(nullable.getOrd(ordNumber)),
	alert: ord.contramap((p: Position) => p.alert)(nullable.getOrd(ordString)),
	country: ord.contramap((p: Position) => p.country)(nullable.getOrd(ordString)),
	custodian: ord.contramap((p: Position) => p.custodian)(nullable.getOrd(ordString)),
};

const ordPositionByLength = pipe(
	ordStringLength,
	ord.contramap((p: Position) => p.productName ?? ''),
);

export const PositionsWidget = memo<PositionsWidgetProps>(({ positions, search, onChangeSearch, fundCurrency }) => {
	const handleSearchChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => onChangeSearch(e.target.value),
		[onChangeSearch],
	);
	const [sort, setSort] = useState<Nullable<SortOrder<Position>>>({
		sortBy: 'productName',
		order: 'ascend',
	});

	const handleChangeTable = useCallback(
		(
			pagination: TablePaginationConfig,
			filters: Record<string, FilterValue | null>,
			sorter: SorterResult<Position> | SorterResult<Position>[],
		) => {
			const sort = Array.isArray(sorter) ? sorter[0] : sorter;
			if (sort?.column && sort.order) {
				setSort({
					order: sort.order,
					sortBy: sort.column.dataIndex as keyof Position,
				});
			} else {
				setSort(null);
			}
		},
		[],
	);

	const sortOrd = useMemo(() => {
		if (sort) {
			const sorter = ords[sort.sortBy];
			if (sorter) {
				return sort.order === 'ascend' ? sorter : ord.getDualOrd(sorter);
			}
		}
		return null;
	}, [sort]);

	const sortedData = useMemo(
		() => (sortOrd ? pipe(positions, remoteData.map(array.sort(sortOrd))) : positions),
		[sortOrd, positions],
	);

	const searchInput = (
		<Input
			type="text"
			value={search}
			className={css.search}
			onChange={handleSearchChange}
			suffix={<SearchOutlined />}
		/>
	);

	const positionNameWidth = useMemo(
		() =>
			pipe(
				sortedData,
				remoteData.map(data => {
					const sizes = pipe(data, array.sort(ordPositionByLength));
					return sizes[Math.floor(sizes.length * 0.9)]?.productName?.length ?? 0;
				}),
				remoteData.getOrElse(() => 0),
				// constants to approximately measure the text width in pixels
				chars => `max(150px, min(${(chars / 23) * 210 + 30}px, 25vw))`,
			),
		[sortedData],
	);
	const columns = useMemo(
		() =>
			makeColumnConfigs(fundCurrency).map(c =>
				c.key === 'productName' ? { ...c, width: positionNameWidth } : c,
			),
		[positionNameWidth, fundCurrency],
	);

	const isMobile = useIsMobile();
	return isMobile ? (
		<Fragment>
			{searchInput}
			{renderRemoteData(positions, {
				success: positions =>
					positions.length ? (
						positions.map((p, i) => (
							<PositionCard position={p} fundCurrency={fundCurrency} key={`card-${i}`} />
						))
					) : (
						<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
					),
			})}
		</Fragment>
	) : (
		<Fragment>
			{searchInput}
			<TableWidget<Position>
				// Fills the viewport below the table so the body owns the vertical scroll
				// and antd pins the header to it (issue #70).
				bodyHeight="fill"
				onChange={handleChangeTable}
				rowKey={(p, index) => `row-${index}`}
				columns={columns /*.map(x => ({ ...x, width: 150 }))*/}
				columnSelector={{
					storageKey: 'positions',
					alwaysVisible: ['productName'],
					defaultHidden: ['openPrice', 'costFifo', 'mtmBasePctOfNav', 'country', 'custodian'],
				}}
				scroll={{ x: 'auto' }}
				dataSource={sortedData}
			/>
		</Fragment>
	);
});

const QUERY = gql`
	query GetProductPositions($productId: String!, $search: String) {
		positions(productId: $productId, search: $search) {
			instrument
			productName
			ticker
			quantity
			side
			currency
			openPrice
			costWavg
			costFifo
			lastPrice
			mtmBase
			mtmBasePctOfNav
			exposureBase
			exposurePctOfNav
			profitLossBase
			alert
			country
			custodian
		}
	}
`;

export const PositionsWidgetContainer = memo(() => {
	const { selectedProduct } = useContext(ProductContext);
	const [search, setSearch] = useState('');
	const { data, loading, error } = useQuery<GetProductPositions, GetProductPositionsVariables>(QUERY, {
		variables: { productId: selectedProduct?.id ?? '', search },
		skip: !selectedProduct,
	});

	const rd = pipe(
		useRemoteData(data, loading, error),
		remoteData.map(rd =>
			rd.positions.map(p => ({
				...p,
				// The export ships these as ratios (0.0278 = 2.78%), both of them.
				exposurePctOfNav: pipe(p.exposurePctOfNav, nullable.map(toPercent)),
				mtmBasePctOfNav: pipe(p.mtmBasePctOfNav, nullable.map(toPercent)),
			})),
		),
	);
	return (
		<PositionsWidget
			positions={rd}
			search={search}
			onChangeSearch={setSearch}
			// The fund's base currency, already fetched by FundSelector's GetProducts
			// query — the `…Base` amounts are denominated in it.
			fundCurrency={selectedProduct?.currency}
		/>
	);
});
